/**
 * TeamLockBanner — shown on every team-scoped page when the active team is a
 * locked secondary team (free coach owns multiple teams but only the first
 * one is fully usable). Tapping "Upgrade" opens Stripe checkout for The
 * Chalkboard.
 *
 * Design note: deliberately calm. No red. Same chalk-rectangular spec the rest
 * of the app uses, with a 2px solid #2E7D52 left accent that matches
 * UpgradeGateCard so the whole upgrade vocabulary feels coherent.
 */
import { Lock } from "lucide-react";
import { toast } from "sonner";

import { startCheckout } from "../../lib/checkout";

export default function TeamLockBanner({ team, sourceScreen }) {
  if (!team || !team.is_locked) return null;

  async function upgrade() {
    try {
      await startCheckout({
        tier: "chalkboard",
        teamId: team.id,
        context: {
          feature_name: "MULTI_TEAM",
          source_screen: sourceScreen || "team_lock_banner",
          current_tier: "pickup",
        },
      });
    } catch (e) {
      toast.error(e.message || "Couldn't open checkout.");
    }
  }

  return (
    <div
      data-testid="team-lock-banner"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        background: "#FFFDF5",
        border: "0.5px solid var(--chalk-border)",
        borderLeft: "2px solid #2E7D52",
        borderRadius: 14,
        padding: "14px 16px",
        marginBottom: 14,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 30, height: 30, borderRadius: 8,
          background: "rgba(46,125,82,0.08)", color: "#2E7D52",
          flexShrink: 0,
        }}
      >
        <Lock size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 14,
            color: "var(--chalk-board)", letterSpacing: "0.02em",
          }}
        >
          This team is locked.
        </div>
        <div
          style={{
            marginTop: 4, fontSize: 13, lineHeight: 1.45,
            color: "var(--chalk-muted)", fontFamily: "var(--font-body)",
          }}
        >
          Managing more than one team is part of The Chalkboard. Your first
          team stays fully active on the free plan — upgrade to unlock the
          rest. $11.99/month, cancel anytime.
        </div>
        <button
          type="button"
          onClick={upgrade}
          data-testid="team-lock-upgrade-cta"
          style={{
            marginTop: 10,
            padding: "9px 14px",
            background: "var(--chalk-board)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontFamily: "var(--font-body)",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Upgrade to unlock
        </button>
      </div>
    </div>
  );
}
